import { useEffect } from 'react';

import { fg } from '@confluence/feature-gating';

import type { ReadPageContent } from './nativeEmbedChildClient.ts';

/** The slice of an editor preset API this hook needs to read the current document. */
type EditorReadContentPresetAPI = {
	core?: { actions: { requestDocumentAsync: () => Promise<unknown> } };
};

/**
 * Registers a mounted editor as the read-content provider for the page it has open — a classic page
 * in edit mode or a live document (including for view-only users). The returned unregister runs as
 * the effect cleanup, so a content change or unmount removes the provider before it could answer for
 * another page. Shared by the embedded classic editor and the full-page editor.
 *
 * @param setReadPageContent from `usePageNativeEmbedClient`; `undefined` outside a native embed, in
 * which case this hook does nothing.
 */
export const useRegisterEditorReadPageContent = ({
	contentId,
	editorPresetAPI,
	setReadPageContent,
}: {
	contentId: string;
	editorPresetAPI: EditorReadContentPresetAPI | undefined;
	setReadPageContent?: (readPageContent: ReadPageContent) => () => void;
}): void => {
	useEffect(() => {
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (!fg('maui_remixable_page_native_embeds')) {
			return;
		}

		return setReadPageContent?.({
			contentId,
			source: 'editor',
			getPageAdf: async () => {
				const pageAdf = await editorPresetAPI?.core?.actions.requestDocumentAsync();
				return pageAdf ? JSON.stringify(pageAdf) : '';
			},
		});
	}, [contentId, editorPresetAPI, setReadPageContent]);
};
